import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { Task } from '@/types/task';
import { useToast } from '@/hooks/use-toast';
import { ToastAction } from '@/components/ui/toast';
import { startOfWeek, endOfWeek, format } from 'date-fns';
import { notificationService } from '@/services/notificationService';
import { getErrorMessage } from '@/lib/networkError';

// Track scheduled task notifications
const scheduledTaskNotifications = new Map<string, NodeJS.Timeout>();

// Schedule a notification 30 minutes before task due time
function scheduleTaskDueNotification(task: Task) {
  if (!task.due_time || !task.day || task.completed) return;
  
  // Cancel any existing notification for this task
  cancelTaskNotification(task.id);
  
  // Parse the due time
  const [hours, minutes] = task.due_time.split(':').map(Number);
  const dueDate = new Date(task.day);
  dueDate.setHours(hours, minutes, 0, 0);
  
  // Calculate notification time (30 minutes before due)
  const notifyAt = new Date(dueDate.getTime() - 30 * 60 * 1000);
  const now = new Date();
  const delay = notifyAt.getTime() - now.getTime();
  
  // Only schedule if the notification time is in the future
  if (delay > 0) {
    const timeoutId = setTimeout(() => {
      notificationService.refreshPermission();
      notificationService.sendTaskDueNotification(task.content);
      scheduledTaskNotifications.delete(task.id);
    }, delay);
    
    scheduledTaskNotifications.set(task.id, timeoutId);
  }
}

// Cancel a scheduled task notification
function cancelTaskNotification(taskId: string) {
  const timeoutId = scheduledTaskNotifications.get(taskId);
  if (timeoutId) {
    clearTimeout(timeoutId);
    scheduledTaskNotifications.delete(taskId);
  }
}

export function useTasks(weekStart?: Date) {
  const { user } = useAuth();
  const { toast } = useToast();
  const [tasks, setTasks] = useState<Task[]>([]);
  const [loading, setLoading] = useState(true);

  // Move incomplete tasks from past days to today
  const rolloverIncompleteTasks = useCallback(async () => {
    if (!user) return;

    const today = format(new Date(), 'yyyy-MM-dd');
    
    try {
      // Find all incomplete tasks from past days (not backlog, not today or future)
      const { data: pastTasks, error: fetchError } = await supabase
        .from('tasks')
        .select('id')
        .eq('user_id', user.id)
        .eq('completed', false)
        .lt('day', today)
        .not('day', 'is', null);

      if (fetchError) throw fetchError;

      if (pastTasks && pastTasks.length > 0) {
        // Update all past incomplete tasks to today
        const { error: updateError } = await supabase
          .from('tasks')
          .update({ day: today })
          .in('id', pastTasks.map(t => t.id));

        if (updateError) throw updateError;
      }
    } catch (error) {
      console.error('Error rolling over tasks:', error);
      throw error; // Let fetchTasks catch handle toast
    }
  }, [user]);

  const fetchTasks = useCallback(async () => {
    if (!user) {
      setTasks([]);
      setLoading(false);
      return;
    }

    try {
      // First, rollover any incomplete tasks from past days
      await rolloverIncompleteTasks();

      // Build query - fetch tasks within week range OR backlog tasks (day is null)
      let query = supabase
        .from('tasks')
        .select('*')
        .eq('user_id', user.id)
        .order('order_index', { ascending: true });

      if (weekStart) {
        const weekEnd = endOfWeek(weekStart, { weekStartsOn: 1 });
        const startDate = format(weekStart, 'yyyy-MM-dd');
        const endDate = format(weekEnd, 'yyyy-MM-dd');
        // Fetch both scheduled tasks for the week AND backlog tasks
        query = query.or(`and(day.gte.${startDate},day.lte.${endDate}),day.is.null`);
      }

      const { data, error } = await query;

      if (error) throw error;
      setTasks((data as Task[]) || []);
    } catch (error) {
      console.error('Error fetching tasks:', error);
      toast({
        title: 'Error loading tasks',
        description: getErrorMessage(error, 'Please try again.'),
        variant: 'destructive',
        action: (
          <ToastAction altText="Retry" onClick={() => fetchTasks()}>
            Retry
          </ToastAction>
        ),
      });
    } finally {
      setLoading(false);
    }
  }, [user, weekStart, toast, rolloverIncompleteTasks]);

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  // When a tag is deleted: clear it from tasks in real time (optimistic), then refetch to stay in sync
  useEffect(() => {
    const handler = (e: Event) => {
      const tagId = (e as CustomEvent<{ tagId: string }>).detail?.tagId;
      if (tagId) {
        setTasks((prev) =>
          prev.map((t) => (t.tag_id === tagId ? { ...t, tag_id: null } : t))
        );
      }
      fetchTasks();
    };
    window.addEventListener('tag-deleted', handler);
    return () => window.removeEventListener('tag-deleted', handler);
  }, [fetchTasks]);

  const addTask = async (
    content: string, 
    day: string | null, 
    duration?: number, 
    dueTime?: string, 
    isSideQuest?: boolean,
    completed?: boolean
  ) => {
    if (!user) return null;

    const maxOrder = tasks.length > 0 ? Math.max(...tasks.map(t => t.order_index)) + 1 : 0;

    try {
      const { data, error } = await supabase
        .from('tasks')
        .insert({
          user_id: user.id,
          content,
          day,
          duration: duration || null,
          due_time: dueTime || null,
          order_index: maxOrder,
          is_side_quest: isSideQuest || false,
          is_main_task: false,
          completed: completed || false,
        })
        .select()
        .single();

      if (error) throw error;
      
      const newTask = data as Task;
      setTasks(prev => [...prev, newTask]);
      
      // Schedule notification if task has a due time
      if (newTask.due_time && !newTask.completed) {
        scheduleTaskDueNotification(newTask);
      }
      
      return newTask;
    } catch (error) {
      console.error('Error adding task:', error);
      toast({
        title: 'Error adding task',
        description: getErrorMessage(error, 'Please try again.'),
        variant: 'destructive',
      });
      return null;
    }
  };

  const addBacklogTask = async (content: string, duration?: number, dueTime?: string) => {
    return addTask(content, null, duration, dueTime, false);
  };

  const setMainTask = async (taskId: string) => {
    // Optimistic update: update state immediately so drop animation is smooth
    setTasks(prev => prev.map(t => ({
      ...t,
      is_main_task: t.id === taskId
    })));

    try {
      // Clear previous main task in DB
      const currentMain = tasks.find(t => t.is_main_task);
      if (currentMain) {
        await supabase
          .from('tasks')
          .update({ is_main_task: false })
          .eq('id', currentMain.id);
      }

      // Set new main task in DB
      const { error } = await supabase
        .from('tasks')
        .update({ is_main_task: true })
        .eq('id', taskId);

      if (error) throw error;
    } catch (error) {
      console.error('Error setting main task:', error);
      fetchTasks();
      toast({
        title: 'Error setting main task',
        description: getErrorMessage(error, 'Please try again.'),
        variant: 'destructive',
      });
    }
  };

  const clearMainTask = async () => {
    const currentMain = tasks.find(t => t.is_main_task);
    if (!currentMain) return;

    // Optimistic update: update state immediately so drop animation is smooth
    setTasks(prev => prev.map(t => ({
      ...t,
      is_main_task: t.id === currentMain.id ? false : t.is_main_task
    })));

    try {
      const { error } = await supabase
        .from('tasks')
        .update({ is_main_task: false })
        .eq('id', currentMain.id);

      if (error) throw error;
    } catch (error) {
      console.error('Error clearing main task:', error);
      fetchTasks();
      toast({
        title: 'Error updating',
        description: getErrorMessage(error, 'Please try again.'),
        variant: 'destructive',
      });
    }
  };

  const moveToToday = async (taskId: string) => {
    const today = format(new Date(), 'yyyy-MM-dd');
    await updateTask(taskId, { day: today });
  };

  const moveToBacklog = async (taskId: string) => {
    await updateTask(taskId, { day: null, is_main_task: false });
  };

  const updateTask = async (id: string, updates: Partial<Task>) => {
    const previous = tasks.find(t => t.id === id);
    if (!previous) return;

    // Optimistic update for single UI-only field changes to prevent flicker
    const singleFieldKeys = ['tag_id', 'folder_id', 'content', 'is_side_quest'];
    const updateKeys = Object.keys(updates);
    const isOptimisticSafe = updateKeys.length === 1 && singleFieldKeys.includes(updateKeys[0]);
    if (isOptimisticSafe) {
      const updatedTask = { ...previous, ...updates };
      setTasks(prev => prev.map(t => t.id === id ? updatedTask : t));
    }

    try {
      const { error } = await supabase
        .from('tasks')
        .update(updates)
        .eq('id', id);

      if (error) throw error;

      const updatedTask = { ...previous, ...updates };
      if (!isOptimisticSafe) {
        setTasks(prev => prev.map(t => t.id === id ? updatedTask : t));
      }
      // Handle notification scheduling using the same post-DB state
      if (updatedTask.completed) {
        cancelTaskNotification(id);
      } else if (updates.due_time !== undefined || updates.day !== undefined) {
        scheduleTaskDueNotification(updatedTask);
      }
    } catch (error) {
      if (isOptimisticSafe) {
        setTasks(prev => prev.map(t => t.id === id ? previous : t));
      }
      console.error('Error updating task:', error);
      toast({
        title: 'Error updating task',
        description: getErrorMessage(error, 'Please try again.'),
        variant: 'destructive',
      });
    }
  };

  const toggleComplete = async (id: string) => {
    const task = tasks.find(t => t.id === id);
    if (!task) return;
    
    const newCompleted = !task.completed;
    await updateTask(id, { completed: newCompleted });
    
    // Cancel notification if marking as complete
    if (newCompleted) {
      cancelTaskNotification(id);
    }
  };

  const deleteTask = async (id: string) => {
    try {
      // Cancel any scheduled notification
      cancelTaskNotification(id);
      
      const { error } = await supabase
        .from('tasks')
        .delete()
        .eq('id', id);

      if (error) throw error;
      
      setTasks(prev => prev.filter(t => t.id !== id));
    } catch (error) {
      console.error('Error deleting task:', error);
      toast({
        title: 'Error deleting task',
        description: getErrorMessage(error, 'Please try again.'),
        variant: 'destructive',
      });
    }
  };

  const reorderTasks = async (reorderedTasks: Task[]) => {
    const updates = reorderedTasks.map((task, index) => ({
      id: task.id,
      order_index: index,
    }));

    // Get IDs of tasks being reordered
    const reorderedIds = new Set(reorderedTasks.map(t => t.id));

    // Optimistic update: only update order_index for reordered tasks; preserve array order
    // so that today vs backlog groups stay correct (sorting by order_index would mix them).
    setTasks(prev => {
      const updatedById = new Map(
        reorderedTasks.map((t, i) => [t.id, { ...t, order_index: i }])
      );
      return prev.map(t => updatedById.get(t.id) ?? t);
    });

    try {
      for (const update of updates) {
        const { error } = await supabase
          .from('tasks')
          .update({ order_index: update.order_index })
          .eq('id', update.id);
        
        if (error) throw error;
      }
    } catch (error) {
      console.error('Error reordering tasks:', error);
      fetchTasks();
      toast({
        title: 'Error reordering tasks',
        description: getErrorMessage(error, 'Please try again.'),
        variant: 'destructive',
      });
    }
  };

  return {
    tasks,
    loading,
    addTask,
    addBacklogTask,
    updateTask,
    toggleComplete,
    deleteTask,
    reorderTasks,
    setMainTask,
    clearMainTask,
    moveToToday,
    moveToBacklog,
    refetch: fetchTasks,
  };
}
